import { useCallback, useEffect, useState } from "preact/hooks";

export type PaginationControl = {
  currentPage: number;
  loadNext?(): void;
  loadPrev?(): void;
  loadFirst?(): void;
};
export type PaginatedList<T> = {
  items: T[];
} & PaginationControl;

/**
 *
 * @param data the result of the requested list
 * @param offset offset id or index
 * @param setOffset function to be call on loadNext or loadFirst to specify the new offset
 * @param requestLimit number of requested rows; the last row is a pagination sentinel
 * @returns the visible items and the controls available for the current page
 */
export function buildPaginatedResult<R>(
  data: readonly R[],
  offset: ListPointer,
  setOffset: (o: R | undefined, direction: "asc" | "dec") => void,
  requestLimit: number,
): PaginatedList<R> {
  const {
    list: items,
    isLastPage,
    isFirstPage,
  } = computePage(data, offset, requestLimit);
  const forwardOrder = offset.displayOrder;
  const backwardOrder = oppositeOrder(forwardOrder);
  return {
    items,
    currentPage: offset.page,
    loadNext: isLastPage
      ? undefined
      : () => {
          if (!items.length) return;
          const id = items[items.length - 1];
          setOffset(id, forwardOrder);
        },
    loadPrev: isFirstPage
      ? undefined
      : () => {
          if (!items.length) return;
          const id = items[0];
          setOffset(id, backwardOrder);
        },
    loadFirst: isFirstPage
      ? undefined
      : () => {
          setOffset(undefined, forwardOrder);
        },
  };
}

function oppositeOrder(order: ListOrder): ListOrder {
  return order === "asc" ? "dec" : "asc";
}

/**
 * based on the pointer and the max result set
 *
 * return relative pos and list to show
 * @param data
 * @param offset
 * @param max
 * @returns
 */
function computePage<R>(data: readonly R[], offset: ListPointer, max: number) {
  // we ask for N but show N-1
  // the last element on the list is to signal if
  // we have more pages after it
  const thereIsMore = data.length == max;
  const result = data.slice();
  if (thereIsMore) {
    result.pop();
  }
  const isForward = offset.order === offset.displayOrder;
  if (!isForward) {
    result.reverse();
  }
  const isFirstPage = isForward ? offset.id === undefined : !thereIsMore;
  const isLastPage = isForward ? !thereIsMore : offset.id === undefined;
  return { list: result, isFirstPage, isLastPage };
}

/**
 * Define the entry point of a list when asking for a resultset.
 *
 * If id is not set then assume the top most entry point.
 */
export type ListOrder = "asc" | "dec";

export type ListPointer = {
  id?: string;
  order: ListOrder;
  displayOrder: ListOrder;
  page: number;
};

export type InitialListPointer = {
  /** Start from this cursor when the query identity changes. */
  id?: string;
  /** Order in which pages and their items are displayed. */
  displayOrder?: ListOrder;
};

/**
 *
 * @param getId given a row type T return the rowId
 * @returns current pointer and a function to move
 */
export function useListPointer<T>(
  getId: (d: T) => string,
  queryIdentity?: string,
  initialPointer: InitialListPointer = {},
): [ListPointer, (p: T | undefined, order: ListPointer["order"]) => void] {
  const initialId = initialPointer.id;
  const displayOrder = initialPointer.displayOrder ?? "dec";
  const makeInitialPointer = useCallback(
    (): ListPointer => ({
      id: initialId,
      order: displayOrder,
      displayOrder,
      page: 1,
    }),
    [displayOrder, initialId],
  );
  const [pointer, setPointer] = useState<ListPointer>(makeInitialPointer);
  useEffect(() => {
    setPointer(makeInitialPointer());
  }, [makeInitialPointer, queryIdentity]);
  const movePointer = useCallback(
    (d: T | undefined, order: ListPointer["order"]) => {
      if (!d) {
        setPointer({
          order: displayOrder,
          displayOrder,
          page: 1,
        });
      } else {
        setPointer((current) => ({
          order,
          displayOrder: current.displayOrder,
          id: getId(d),
          page:
            order === current.displayOrder
              ? current.page + 1
              : Math.max(1, current.page - 1),
        }));
      }
    },
    [displayOrder, getId],
  );
  return [pointer, movePointer];
}
